import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { cachedFetch } from '../utils/apiCache';
import { API_BASE } from '../utils/apiBase';

const API_URL = API_BASE;
const YOUTUBE_TTL = 3 * 60 * 1000; // 3 minutes — YouTube data rarely changes

// Default placeholder images
const PLACEHOLDER_LIGHT = '/assets/placeholders/video_default_light.jpg';
const PLACEHOLDER_DARK = '/assets/placeholders/video_default_dark.jpg';

const YouTubeContext = createContext(null);

export const useYouTube = () => {
  const context = useContext(YouTubeContext);
  if (!context) {
    throw new Error('useYouTube must be used within a YouTubeProvider');
  }
  return context;
};

export const YouTubeProvider = ({ children }) => {
  // State
  const [latestVideo, setLatestVideo] = useState(null);
  const [recentVideos, setRecentVideos] = useState([]);
  const [liveStatus, setLiveStatus] = useState({
    live_status: false,
    live_videoId: null,
    live_title: null,
    live_thumbnail: null,
    embedUrl: null
  });
  const [scheduledLive, setScheduledLive] = useState({
    enabled: false,
    startTime: null,
    endTime: null,
    title: 'Culte en direct',
    description: "Rejoignez-nous pour un moment de louange et d'enseignement"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all YouTube data
  const fetchYouTubeData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const data = await cachedFetch(`${API_URL}/api/youtube/data`, {
        ttl: YOUTUBE_TTL,
        forceRefresh,
      });
      
      // Update state with fetched data
      if (data.latestVideo) {
        setLatestVideo({
          id: data.latestVideo.videoId,
          videoId: data.latestVideo.videoId,
          title: data.latestVideo.title,
          description: data.latestVideo.description,
          thumbnail: data.latestVideo.thumbnail || PLACEHOLDER_LIGHT,
          publishDate: data.latestVideo.publishDate,
          duration: data.latestVideo.duration,
          embedUrl: data.latestVideo.embedUrl
        });
      }
      
      if (data.liveStatus) {
        setLiveStatus(data.liveStatus);
      }
      
      if (data.scheduledLive) {
        setScheduledLive(data.scheduledLive);
      }
      
      if (data.recentVideos) {
        setRecentVideos(data.recentVideos.map(video => ({
          id: video.videoId,
          videoId: video.videoId,
          title: video.title,
          description: video.description,
          thumbnail: video.thumbnail || PLACEHOLDER_LIGHT,
          publishDate: video.publishDate,
          duration: video.duration,
          embedUrl: video.embedUrl
        })));
      }
    } catch (err) {
      console.error('Error fetching YouTube data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check live status only (short TTL — polled every 60s, cache for 50s)
  const checkLiveStatus = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API_URL}/api/youtube/live-status`, {
        ttl: 50 * 1000,
      });
      setLiveStatus(data);
    } catch (err) {
      console.error('Error checking live status:', err);
    }
  }, []);

  // Get latest video only
  const getLatestVideo = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API_URL}/api/youtube/latest-message`, {
        ttl: YOUTUBE_TTL,
      });
      setLatestVideo({
        id: data.videoId,
        videoId: data.videoId,
        title: data.title,
        description: data.description,
        thumbnail: data.thumbnail || PLACEHOLDER_LIGHT,
        publishDate: data.publishDate,
        duration: data.duration,
        embedUrl: data.embedUrl
      });
      return data;
    } catch (err) {
      console.error('Error fetching latest video:', err);
    }
    return null;
  }, []);

  // Determine if live section should be shown
  const shouldShowLive = useCallback(() => {
    // If actually live, show live
    if (liveStatus.live_status) {
      return { show: true, type: 'live', data: liveStatus };
    }
    
    // Check scheduled live
    if (scheduledLive.enabled && scheduledLive.startTime && scheduledLive.endTime) {
      const now = new Date();
      const start = new Date(scheduledLive.startTime);
      const end = new Date(scheduledLive.endTime);
      
      if (now >= start && now <= end) {
        return { show: true, type: 'scheduled', data: scheduledLive };
      }
    }
    
    return { show: false, type: null, data: null };
  }, [liveStatus, scheduledLive]);

  // Get thumbnail with fallback
  const getThumbnail = useCallback((video, darkMode = false) => {
    if (video?.thumbnail) {
      return video.thumbnail;
    }
    return darkMode ? PLACEHOLDER_DARK : PLACEHOLDER_LIGHT;
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchYouTubeData();
    
    // Poll for live status every 60 seconds
    const liveInterval = setInterval(checkLiveStatus, 60000);
    
    return () => {
      clearInterval(liveInterval);
    };
  }, [fetchYouTubeData, checkLiveStatus]);

  const value = useMemo(() => ({
    latestVideo,
    recentVideos,
    liveStatus,
    scheduledLive,
    loading,
    error,
    fetchYouTubeData,
    getLatestVideo,
    checkLiveStatus,
    shouldShowLive,
    getThumbnail,
    PLACEHOLDER_LIGHT,
    PLACEHOLDER_DARK
  }), [latestVideo, recentVideos, liveStatus, scheduledLive, loading, error,
    fetchYouTubeData, getLatestVideo, checkLiveStatus, shouldShowLive, getThumbnail]);

  return (
    <YouTubeContext.Provider value={value}>
      {children}
    </YouTubeContext.Provider>
  );
};

export default YouTubeContext;
